import * as marketing from "@alm/domain/marketing";
import { CANONICAL_ORIGIN, PEOPLE_ORIGIN } from "./origins";
import { ORGANIZATION, SITE_NAME, WEBSITE } from "./ppl-schema";

export { PEOPLE_ORIGIN };

/** PPL's arm canonicalises to ppl-ai.com from whichever host served it; any other page to its own
 * site. */
function canonicalOrigin(version: marketing.SiteVersion | null): string {
  return version === "v2" ? PEOPLE_ORIGIN : CANONICAL_ORIGIN;
}

/** The arm's page the live site's `/features` duplicates: PPL names it after the chapter it
 * carries. */
function peoplePath(basePath: string): string {
  return basePath === "/features" ? "/operating-system" : basePath;
}

export interface MarketingSeo {
  title: string;
  description?: string;
  /** Path only, e.g. "/pricing". Defaults to the route's own path. */
  path: string;
  image?: string;
  imageAlt?: string;
  /** A film the page carries, for the card: absolute URL, MIME type and size. */
  video?: { url: string; type: string; width: number; height: number };
  /** The page's own JSON-LD nodes, without `@context`. On PPL's arm they are joined to the
   * Organization and the WebSite in one `@graph`, so a node that says `publisher: { "@id":
   * ORGANIZATION_ID }` resolves on the page it is on; elsewhere they are emitted as they are. */
  schema?: readonly object[];
}

/** A marketing page's head, emitted by the route's `head()` so it is in the HTML a crawler
 * receives. PEOPLE's arm is a site of its own at ppl-ai.com: its pages declare their address there
 * as canonical, carry the site's structured data, and point answer engines at its llms.txt. */
export function marketingHead({
  title,
  description,
  path,
  image,
  imageAlt,
  video,
  schema = [],
}: MarketingSeo) {
  const { version, basePath } = marketing.splitSiteVersion(path);
  const origin = canonicalOrigin(version);
  const canonical = origin + (origin === PEOPLE_ORIGIN ? peoplePath(basePath) : basePath);
  /* Each site's own card. One build serves both, and `og-image.png` is the
     live site's AssistedCare card: until PPL had one of its own, every link
     to ppl-ai.com unfurled with the other company's name on it. */
  const img = image ?? `${origin}${origin === PEOPLE_ORIGIN ? "/og-ppl.png" : "/og-image.png"}`;
  const alt = imageAlt ?? title;
  const people = version === "v2";
  const nodes = people ? [ORGANIZATION, WEBSITE, ...schema] : schema;
  return {
    meta: [
      { title },
      ...(description ? [{ name: "description", content: description }] : []),
      ...(people ? [{ property: "og:site_name", content: SITE_NAME }] : []),
      { property: "og:title", content: title },
      ...(description ? [{ property: "og:description", content: description }] : []),
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonical },
      { property: "og:image", content: img },
      { property: "og:image:alt", content: alt },
      ...(video
        ? [
            { property: "og:video", content: video.url },
            { property: "og:video:secure_url", content: video.url },
            { property: "og:video:type", content: video.type },
            { property: "og:video:width", content: String(video.width) },
            { property: "og:video:height", content: String(video.height) },
          ]
        : []),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      ...(description ? [{ name: "twitter:description", content: description }] : []),
      { name: "twitter:image", content: img },
      { name: "twitter:image:alt", content: alt },
    ],
    links: [
      { rel: "canonical", href: canonical },
      ...(people
        ? [{ rel: "alternate", type: "text/plain", href: `${PEOPLE_ORIGIN}/llms.txt` }]
        : []),
    ],
    scripts:
      nodes.length === 0
        ? []
        : [
            {
              type: "application/ld+json",
              children: JSON.stringify({ "@context": "https://schema.org", "@graph": nodes }),
            },
          ],
  };
}
